import React, { useState, useEffect } from 'react';
import { styled } from "@mui/material/styles";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import Box from "@mui/material/Box";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Divider from "@mui/material/Divider";
import Badge from "@mui/material/Badge";
import Tooltip from "@mui/material/Tooltip";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import Chip from '@mui/material/Chip';
import { DRAWER_WIDTH, AREA_OPTIONS, DETECTION_TYPES } from '../../utils/constants';
import { isAdminUser, getAreaDisplayName } from '../../utils/authService';

// IMPORTS - Notification components
import NotificationPopover from '../notifications/NotificationPopover';
import AudioNotificationSettings from '../notifications/AudioNotificationSettings';

import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';

// Enhanced Styled components with modern design
const StyledAppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  height: "70px",
  background: "linear-gradient(135deg, #1a2332 0%, #243447 100%)",
  borderBottom: "2px solid rgba(26, 188, 156, 0.2)",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
  backdropFilter: "blur(10px)",
  color: "white",
  alignContent: "center",
  // Always offset by the collapsed drawer rail so the AppBar never overlaps it.
  marginLeft: theme.spacing(7),
  width: `calc(100% - ${theme.spacing(7)})`,
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(9),
    width: `calc(100% - ${theme.spacing(9)})`,
  },
  ...(open && {
    marginLeft: DRAWER_WIDTH,
    width: `calc(100% - ${DRAWER_WIDTH}px)`,
    [theme.breakpoints.up("sm")]: {
      marginLeft: DRAWER_WIDTH,
      width: `calc(100% - ${DRAWER_WIDTH}px)`,
    },
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  margin: theme.spacing(0, 1),
  minWidth: 120,
  "& .MuiInputBase-root": {
    color: "white",
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    borderRadius: "8px",
    fontSize: "0.9rem",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.12)",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255, 255, 255, 0.2)",
      borderWidth: "1px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(26, 188, 156, 0.5)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#1ABC9C",
      borderWidth: "2px",
    },
  },
  "& .MuiSelect-icon": {
    color: "rgba(255, 255, 255, 0.7)",
  },
}));

const FilterLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  color: "rgba(255, 255, 255, 0.8)",
  marginRight: theme.spacing(0.5),
  fontWeight: 500,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  whiteSpace: "nowrap"
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  borderRadius: '12px',
  padding: '6px 12px',
  margin: '0 4px',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    borderColor: 'rgba(26, 188, 156, 0.3)',
  }
}));

const BrandSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const StatusIndicator = styled(Box)(({ theme, active }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: active ? '#1ABC9C' : '#95a5a6',
  boxShadow: active ? '0 0 8px rgba(26, 188, 156, 0.6)' : 'none',
  animation: active ? 'pulse 2s infinite' : 'none',
  '@keyframes pulse': {
    '0%': {
      boxShadow: '0 0 0 0 rgba(26, 188, 156, 0.7)',
    },
    '70%': {
      boxShadow: '0 0 0 8px rgba(26, 188, 156, 0)',
    },
    '100%': {
      boxShadow: '0 0 0 0 rgba(26, 188, 156, 0)',
    },
  },
}));

// Enhanced PlaybackControl Component
const PlaybackControl = ({
  isPaused,
  togglePause,
  bufferedEventsCount,
  pausedTime,
  isMobile
}) => {
  const getPauseDuration = () => {
    if (!isPaused || !pausedTime) return '';

    const diffMs = Date.now() - pausedTime;
    const minutes = Math.floor(diffMs / 60000);
    const seconds = Math.floor((diffMs % 60000) / 1000);

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        backgroundColor: isPaused ? 'rgba(231, 76, 60, 0.1)' : 'rgba(26, 188, 156, 0.1)',
        borderRadius: '20px',
        padding: '8px 16px',
        margin: '0 16px',
        border: `2px solid ${isPaused ? 'rgba(231, 76, 60, 0.3)' : 'rgba(26, 188, 156, 0.3)'}`,
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
        }
      }}
    >
      <StatusIndicator active={!isPaused} />

      <Tooltip title={isPaused ? "Resume real-time alerts" : "Pause real-time alerts"}>
        <IconButton
          onClick={togglePause}
          size="small"
          sx={{
            color: isPaused ? '#e74c3c' : '#1ABC9C',
            mx: 1,
            '&:hover': {
              backgroundColor: isPaused ? 'rgba(231, 76, 60, 0.1)' : 'rgba(26, 188, 156, 0.1)',
              transform: 'scale(1.1)'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          {isPaused ? (
            <Badge
              badgeContent={bufferedEventsCount}
              color="error"
              max={999}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.7rem',
                  minWidth: '18px',
                  height: '18px'
                }
              }}
            >
              <PlayArrowIcon fontSize="small" />
            </Badge>
          ) : (
            <PauseIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>

      {!isMobile && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <Typography
            variant="caption"
            sx={{
              color: isPaused ? '#e74c3c' : '#1ABC9C',
              lineHeight: 1,
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
          >
            {isPaused ? `PAUSED ${getPauseDuration()}` : 'LIVE'}
          </Typography>
          {isPaused && bufferedEventsCount > 0 && (
            <Typography
              variant="caption"
              sx={{
                color: '#e74c3c',
                lineHeight: 1,
                fontSize: '0.7rem',
                opacity: 0.8
              }}
            >
              {bufferedEventsCount} buffered
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

// Enhanced Detection Filter Component
const DetectionFilter = ({ selectedDetection, handleDetectionChange, isMobile }) => {
  const detectionOptions = [
    { id: null, title: "All Types" },
    ...DETECTION_TYPES
  ];

  return (
    <FilterContainer sx={{ width: isMobile ? '100%' : 'auto', my: isMobile ? 1 : 0 }}>
      <FilterLabel>Detection</FilterLabel>
      <StyledFormControl sx={{ flexGrow: isMobile ? 1 : 0, minWidth: 140 }}>
        <Select
          value={selectedDetection}
          onChange={handleDetectionChange}
          displayEmpty
          renderValue={() => {
            const selected = detectionOptions.find(opt => opt.id === selectedDetection);
            return selected ? selected.title : "All Types";
          }}
          size="small"
          sx={{ fontSize: "0.85rem" }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
                backgroundColor: '#1e2a38',
                color: 'white',
                border: '1px solid rgba(26, 188, 156, 0.2)',
                borderRadius: '8px',
                backdropFilter: 'blur(10px)'
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
          }}
        >
          {detectionOptions.map(option => (
            <MenuItem
              key={option.id || 'all'}
              value={option.id}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(26, 188, 156, 0.1)'
                }
              }}
            >
              {option.title}
            </MenuItem>
          ))}
        </Select>
      </StyledFormControl>
    </FilterContainer>
  );
};

// Enhanced Area Filter Component
const AreaFilter = ({ selectedArea, handleAreaChange, isMobile }) => {
  const [isAdmin, setIsAdmin] = useState(true);
  const [areaDisplayName, setAreaDisplayName] = useState('All Areas');

  useEffect(() => {
    const adminStatus = isAdminUser();
    setIsAdmin(adminStatus);
    const displayName = getAreaDisplayName();
    setAreaDisplayName(displayName);
  }, []);

  if (!isAdmin) {
    return (
      <FilterContainer sx={{ width: isMobile ? '100%' : 'auto', my: isMobile ? 1 : 0 }}>
        <FilterLabel>Area</FilterLabel>
        <Chip
          label={areaDisplayName}
          sx={{
            backgroundColor: 'rgba(26, 188, 156, 0.2)',
            color: '#1ABC9C',
            fontWeight: 600,
            border: '1px solid rgba(26, 188, 156, 0.5)',
            height: '36px',
            fontSize: '0.9rem',
            '&:hover': {
              backgroundColor: 'rgba(26, 188, 156, 0.3)',
              borderColor: '#1ABC9C'
            }
          }}
        />
      </FilterContainer>
    );
  }

  const handleAreaSelectionChange = (event) => {
    const value = event.target.value;

    if (value.includes("All")) {
      handleAreaChange({ target: { value: "All" } });
      return;
    }

    if (value.length === 0) {
      handleAreaChange({ target: { value: "All" } });
      return;
    }

    handleAreaChange({ target: { value } });
  };

  const renderAreaValue = (selected) => {
    if (selected === "All") {
      return "All Areas";
    }

    if (!Array.isArray(selected) || selected.length === 0) {
      return "All Areas";
    }

    if (selected.length === 1) {
      const option = AREA_OPTIONS.find(opt => opt.value === selected[0]);
      return option ? option.label : selected[0];
    }

    return `${selected.length} Areas`;
  };

  const handleClearAreaSelections = (e) => {
    e.stopPropagation();
    handleAreaChange({ target: { value: "All" } });
  };

  const getSelectedCount = () => {
    if (selectedArea === "All" || !Array.isArray(selectedArea)) return 0;
    return selectedArea.length;
  };

  return (
    <FilterContainer sx={{ width: isMobile ? '100%' : 'auto', my: isMobile ? 1 : 0 }}>
      <FilterLabel>Area</FilterLabel>
      <StyledFormControl sx={{ minWidth: 120, flexGrow: isMobile ? 1 : 0 }}>
        <Select
          multiple
          value={selectedArea === "All" ? [] : Array.isArray(selectedArea) ? selectedArea : [selectedArea]}
          onChange={handleAreaSelectionChange}
          displayEmpty
          renderValue={renderAreaValue}
          size="small"
          sx={{
            fontSize: "0.85rem",
            maxWidth: isMobile ? "100%" : 180,
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }
          }}
          endAdornment={
            selectedArea !== "All" && getSelectedCount() > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                <Chip
                  size="small"
                  label={getSelectedCount()}
                  sx={{
                    height: '18px',
                    fontSize: '0.7rem',
                    backgroundColor: 'rgba(26, 188, 156, 0.2)',
                    color: '#1ABC9C',
                    mr: 0.5
                  }}
                />
                <IconButton
                  size="small"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    p: 0.5,
                    '&:hover': {
                      color: '#e74c3c',
                      backgroundColor: 'rgba(231, 76, 60, 0.1)'
                    }
                  }}
                  onClick={handleClearAreaSelections}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Box>
            )
          }
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
                backgroundColor: '#1e2a38',
                color: 'white',
                border: '1px solid rgba(26, 188, 156, 0.2)',
                borderRadius: '8px',
                backdropFilter: 'blur(10px)'
              },
            },
            keepMounted: true,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
          }}
        >
          <MenuItem
            key="all"
            value="All"
            sx={{
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              '&:hover': {
                backgroundColor: 'rgba(26, 188, 156, 0.1)'
              }
            }}
          >
            <Checkbox
              checked={selectedArea === "All"}
              sx={{
                '& .MuiSvgIcon-root': { color: '#1ABC9C' },
                '&.Mui-checked': {
                  color: '#1ABC9C'
                }
              }}
            />
            <ListItemText primary="All Areas" />
          </MenuItem>
          {AREA_OPTIONS.filter(option => option.value !== "All").map(option => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(26, 188, 156, 0.1)'
                }
              }}
            >
              <Checkbox
                checked={
                  selectedArea !== "All" &&
                  Array.isArray(selectedArea) &&
                  selectedArea.indexOf(option.value) > -1
                }
                sx={{
                  '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' },
                  '&.Mui-checked': {
                    color: '#1ABC9C'
                  }
                }}
              />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
      </StyledFormControl>
    </FilterContainer>
  );
};

// Enhanced Mobile Filter Drawer Component
const MobileFilterDrawer = ({
  open,
  toggleDrawer,
  selectedDetection,
  handleDetectionChange,
  selectedArea,
  handleAreaChange
}) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={toggleDrawer}
      PaperProps={{
        sx: {
          background: "linear-gradient(135deg, #1a2332 0%, #243447 100%)",
          color: "white",
          width: "85%",
          maxWidth: "380px",
          borderLeft: "2px solid rgba(26, 188, 156, 0.2)",
          backdropFilter: "blur(10px)"
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1ABC9C' }}>
            Filters
          </Typography>
          <IconButton
            color="inherit"
            onClick={toggleDrawer}
            aria-label="close drawer"
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(231, 76, 60, 0.1)',
                color: '#e74c3c'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: "rgba(26, 188, 156, 0.2)", mb: 3 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <DetectionFilter
            selectedDetection={selectedDetection}
            handleDetectionChange={handleDetectionChange}
            isMobile={true}
          />
          <AreaFilter
            selectedArea={selectedArea}
            handleAreaChange={handleAreaChange}
            isMobile={true}
          />
        </Box>
      </Box>
    </Drawer>
  );
};

// Enhanced Main AppBar Component
const AppBar = ({
  open,
  toggleDrawer,
  selectedArea,
  handleAreaChange,
  selectedCategory,
  handleCategoryChange,
  selectedDetection,
  handleDetectionChange,
  isReportsView,
  isPaused,
  togglePause,
  bufferedEventsCount,
  pausedTime,
  lockView,
  onToggleLockView,
  notifications = [],
  totalActiveEvents = 0,
  onNotificationClick,
  onNotificationClose
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const toggleFilterDrawer = () => {
    setFilterDrawerOpen(!filterDrawerOpen);
  };

  return (
    <>
      <StyledAppBar position="fixed" open={open}>
        <Toolbar sx={{
          justifyContent: "space-between",
          paddingX: 3,
          minHeight: "70px !important",
          alignItems: "center"
        }}>
          {/* Left section with menu */}
          <BrandSection>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={toggleDrawer}
              edge="start"
              sx={{
                marginRight: 1,
                padding: '8px',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: 'rgba(26, 188, 156, 0.1)',
                  color: '#1ABC9C'
                },
                ...(open && { display: 'none' }),
              }}
            >
              <MenuIcon />
            </IconButton>
          </BrandSection>

          {/* Center section with title and status */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexGrow: 1,
            minWidth: 0,
            textAlign: 'center'
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
              <Typography
                variant="h4"
                component="div"
                noWrap
                sx={{
                  fontWeight: 700,
                  fontSize: isMobile ? "1.15rem" : "1.5rem",
                  background: 'linear-gradient(45deg, #ffffff 30%, #1ABC9C 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '0.5px',
                  whiteSpace: 'nowrap'
                }}
              >
                {isReportsView ? "Analytics Reports" : "Alert Dashboard"}
              </Typography>

              {!isReportsView && !isMobile && (
                <Typography
                  variant="caption"
                  noWrap
                  sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    mt: 0.5,
                    whiteSpace: 'nowrap'
                  }}
                >
                  Real-time monitoring system
                </Typography>
              )}
            </Box>

            {!isReportsView && (
              <PlaybackControl
                isPaused={isPaused}
                togglePause={togglePause}
                bufferedEventsCount={bufferedEventsCount}
                pausedTime={pausedTime}
                isMobile={isMobile}
              />
            )}
          </Box>

          {/* Right section - Updated with audio settings and notifications */}
          {/* Right section - Updated with lock toggle, audio settings and notifications */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>

            {/* Lock View Toggle */}
            {!isReportsView && (
              <Tooltip title={lockView
                ? "View locked — cards won't swap on notification click"
                : "View unlocked — cards auto-swap to show alert type"
              }>
                <IconButton
                  onClick={onToggleLockView}
                  size="small"
                  sx={{
                    color: lockView ? '#1ABC9C' : 'rgba(255,255,255,0.4)',
                    backgroundColor: lockView ? 'rgba(26,188,156,0.12)' : 'transparent',
                    border: lockView
                      ? '1px solid rgba(26,188,156,0.4)'
                      : '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '8px',
                    padding: '8px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(26,188,156,0.15)',
                      color: '#1ABC9C',
                      borderColor: 'rgba(26,188,156,0.4)'
                    }
                  }}
                >
                  {lockView
                    ? <LockIcon fontSize="small" />
                    : <LockOpenIcon fontSize="small" />
                  }
                </IconButton>
              </Tooltip>
            )}

            {/* Audio Settings (Compact Mode) */}
            <AudioNotificationSettings compact={true} />

            {/* Notifications Popover */}
            <NotificationPopover
              notifications={notifications}
              totalActiveEvents={totalActiveEvents}
              onNotificationClick={onNotificationClick}
              onNotificationClose={onNotificationClose}
            />

            {!isReportsView && !isMobile && (
              <>
                <DetectionFilter
                  selectedDetection={selectedDetection}
                  handleDetectionChange={handleDetectionChange}
                  isMobile={false}
                />
                <AreaFilter
                  selectedArea={selectedArea}
                  handleAreaChange={handleAreaChange}
                  isMobile={false}
                />
              </>
            )}

            {!isReportsView && isMobile && (
              <IconButton
                color="inherit"
                aria-label="open filters"
                onClick={toggleFilterDrawer}
                edge="end"
                sx={{
                  padding: '8px',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(26, 188, 156, 0.1)',
                    color: '#1ABC9C'
                  }
                }}
              >
                <Badge
                  badgeContent={
                    (selectedDetection ? 1 : 0) +
                    (selectedArea !== "All" && Array.isArray(selectedArea) ? selectedArea.length : 0)
                  }
                  color="primary"
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: '#1ABC9C',
                      color: 'white'
                    }
                  }}
                >
                  <FilterListIcon />
                </Badge>
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </StyledAppBar>

      {!isReportsView && isMobile && (
        <MobileFilterDrawer
          open={filterDrawerOpen}
          toggleDrawer={toggleFilterDrawer}
          selectedDetection={selectedDetection}
          handleDetectionChange={handleDetectionChange}
          selectedArea={selectedArea}
          handleAreaChange={handleAreaChange}
        />
      )}
    </>
  );
};

export default AppBar;

